import { type ChangeEvent, useCallback, useMemo, useRef, useState } from "react";

import { defaultScoringConfig, type ScoringConfig } from "@/features/simulation/runSimulation";
import { type StringTranslationKey, useTranslations } from "@/i18n";
import { useStore } from "@/store/store";
import { DEFAULT_MULTIPLIER_CONFIG } from "@/store/types";
import type { MultiplierConfig } from "@/types";
import {
  ActionRow,
  CardHeader,
  CardSubtitle,
  CardTitle,
  GlassCard,
  GlassNumberInput,
  ListGap,
  ParamHint,
  ParamLabel,
  ParamRow,
  PillBtn,
  PillBtnDanger,
  ToggleBody,
  ToggleDesc,
  ToggleDot,
  ToggleLabel,
  ToggleRow,
} from "@/ui/styled";
import { theme } from "@/ui/theme";

type TunableParam = {
  parentKey: keyof MultiplierConfig;
  subKey: string;
  label: string;
  min: number;
  max: number;
  step: number;
  hintKey?: StringTranslationKey;
};

type MultiplierInfo = {
  key: keyof MultiplierConfig;
  labelKey: StringTranslationKey;
  descKey: StringTranslationKey;
  params: TunableParam[];
};

const MULTIPLIERS: MultiplierInfo[] = [
  {
    key: "scale",
    labelKey: "multiplier.scale",
    descKey: "multiplier.scaleDesc",
    params: [
      {
        parentKey: "scale",
        subKey: "factor",
        label: "Factor",
        min: 1,
        max: 100,
        step: 1,
        hintKey: "hint.scaleFactor",
      },
    ],
  },
  {
    key: "weight",
    labelKey: "multiplier.weight",
    descKey: "multiplier.weightDesc",
    params: [],
  },
  {
    key: "upset",
    labelKey: "multiplier.upset",
    descKey: "multiplier.upsetDesc",
    params: [],
  },
  {
    key: "streak",
    labelKey: "multiplier.streak",
    descKey: "multiplier.streakDesc",
    params: [],
  },
  {
    key: "fatigue",
    labelKey: "multiplier.fatigue",
    descKey: "multiplier.fatigueDesc",
    params: [
      {
        parentKey: "fatigue",
        subKey: "halfLife",
        label: "Half-life (games)",
        min: 5,
        max: 50,
        step: 1,
        hintKey: "hint.fatigueHalfLife",
      },
    ],
  },
  {
    key: "momentum",
    labelKey: "multiplier.momentum",
    descKey: "multiplier.momentumDesc",
    params: [
      {
        parentKey: "momentum",
        subKey: "factor",
        label: "Factor",
        min: 0.01,
        max: 0.2,
        step: 0.01,
        hintKey: "hint.momentumFactor",
      },
      {
        parentKey: "momentum",
        subKey: "cap",
        label: "Cap",
        min: 0.1,
        max: 1.0,
        step: 0.05,
        hintKey: "hint.momentumCap",
      },
    ],
  },
  {
    key: "comeback",
    labelKey: "multiplier.comeback",
    descKey: "multiplier.comebackDesc",
    params: [
      {
        parentKey: "comeback",
        subKey: "threshold",
        label: "Threshold (rating deficit)",
        min: 5,
        max: 40,
        step: 1,
        hintKey: "hint.comebackThreshold",
      },
      {
        parentKey: "comeback",
        subKey: "maxBonus",
        label: "Max bonus",
        min: 0.1,
        max: 1.0,
        step: 0.05,
        hintKey: "hint.comebackMaxBonus",
      },
    ],
  },
  {
    key: "consistency",
    labelKey: "multiplier.consistency",
    descKey: "multiplier.consistencyDesc",
    params: [
      {
        parentKey: "consistency",
        subKey: "sigmaThreshold",
        label: "σ threshold",
        min: 2,
        max: 12,
        step: 0.5,
        hintKey: "hint.consistencySigmaThreshold",
      },
      {
        parentKey: "consistency",
        subKey: "bonus",
        label: "Bonus",
        min: 0.05,
        max: 0.5,
        step: 0.05,
        hintKey: "hint.consistencyBonus",
      },
    ],
  },
  {
    key: "closeness",
    labelKey: "multiplier.closeness",
    descKey: "multiplier.closenessDesc",
    params: [
      {
        parentKey: "closeness",
        subKey: "maxDelta",
        label: "Max Δ",
        min: 0.1,
        max: 2.0,
        step: 0.1,
        hintKey: "hint.closenessMaxDelta",
      },
      {
        parentKey: "closeness",
        subKey: "bonus",
        label: "Bonus",
        min: 0.05,
        max: 0.5,
        step: 0.05,
        hintKey: "hint.closenessBonus",
      },
    ],
  },
  {
    key: "variety",
    labelKey: "multiplier.variety",
    descKey: "multiplier.varietyDesc",
    params: [
      {
        parentKey: "variety",
        subKey: "decay",
        label: "Decay (games)",
        min: 1,
        max: 10,
        step: 1,
        hintKey: "hint.varietyDecay",
      },
      {
        parentKey: "variety",
        subKey: "bonus",
        label: "Max bonus",
        min: 0.05,
        max: 0.5,
        step: 0.05,
        hintKey: "hint.varietyBonus",
      },
    ],
  },
  {
    key: "rivalry",
    labelKey: "multiplier.rivalry",
    descKey: "multiplier.rivalryDesc",
    params: [
      {
        parentKey: "rivalry",
        subKey: "lookback",
        label: "Lookback (matches)",
        min: 3,
        max: 30,
        step: 1,
        hintKey: "hint.rivalryLookback",
      },
      {
        parentKey: "rivalry",
        subKey: "bonus",
        label: "Max bonus",
        min: 0.05,
        max: 0.5,
        step: 0.05,
        hintKey: "hint.rivalryBonus",
      },
    ],
  },
];

/** Map a GA ScoringConfig to MultiplierConfig */
export const scoringConfigToMultiplierConfig = (sc: ScoringConfig): MultiplierConfig => ({
  scale: {
    enabled: true,
    factor: sc.scale,
  },
  weight: { enabled: true },
  upset: { enabled: sc.upsetEnabled ?? false },
  streak: { enabled: sc.streakEnabled ?? false },
  fatigue: {
    enabled: sc.fatigueEnabled ?? false,
    halfLife: sc.fatigueHalfLife ?? 20,
  },
  momentum: {
    enabled: sc.momentumEnabled ?? false,
    factor: sc.momentumFactor ?? 0.05,
    cap: sc.momentumCap ?? 0.5,
  },
  comeback: {
    enabled: sc.comebackEnabled ?? false,
    threshold: sc.comebackThreshold ?? 20,
    maxBonus: sc.comebackMaxBonus ?? 0.25,
  },
  consistency: {
    enabled: sc.consistencyEnabled ?? false,
    sigmaThreshold: sc.consistencySigmaThreshold ?? 6,
    bonus: sc.consistencyBonus ?? 0.1,
  },
  closeness: {
    enabled: sc.closenessEnabled ?? false,
    maxDelta: sc.closenessMaxDelta ?? 1,
    bonus: sc.closenessBonus ?? 0.1,
  },
  variety: {
    enabled: sc.varietyEnabled ?? true,
    decay: sc.varietyDecay ?? 3,
    bonus: sc.varietyBonus ?? 0.2,
  },
  rivalry: {
    enabled: sc.rivalryEnabled ?? true,
    lookback: sc.rivalryLookback ?? 10,
    bonus: sc.rivalryBonus ?? 0.25,
  },
});

/** Map MultiplierConfig to ScoringConfig (for export or GA seed) */
export const multiplierConfigToScoringConfig = (mc: MultiplierConfig): ScoringConfig => ({
  ...defaultScoringConfig,
  upsetEnabled: mc.upset.enabled,
  streakEnabled: mc.streak.enabled,
  fatigueEnabled: mc.fatigue.enabled,
  fatigueHalfLife: mc.fatigue.halfLife,
  momentumEnabled: mc.momentum.enabled,
  momentumFactor: mc.momentum.factor,
  momentumCap: mc.momentum.cap,
  comebackEnabled: mc.comeback.enabled,
  comebackThreshold: mc.comeback.threshold,
  comebackMaxBonus: mc.comeback.maxBonus,
  consistencyEnabled: mc.consistency.enabled,
  consistencySigmaThreshold: mc.consistency.sigmaThreshold,
  consistencyBonus: mc.consistency.bonus,
  closenessEnabled: mc.closeness.enabled,
  closenessMaxDelta: mc.closeness.maxDelta,
  closenessBonus: mc.closeness.bonus,
  varietyEnabled: mc.variety.enabled,
  varietyDecay: mc.variety.decay,
  varietyBonus: mc.variety.bonus,
  rivalryEnabled: mc.rivalry.enabled,
  rivalryLookback: mc.rivalry.lookback,
  rivalryBonus: mc.rivalry.bonus,
});

export default function MultiplierConfigPanel() {
  const config = useStore((s) => s.multiplierConfig);
  const matchesById = useStore((s) => s.matches);
  const updateMultiplierConfig = useStore((s) => s.updateMultiplierConfig);
  const recalculateAll = useStore((s) => s.recalculateAll);
  const { t } = useTranslations();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [recalcMsg, setRecalcMsg] = useState<string | null>(null);

  const enabledCount = useMemo(() => {
    return MULTIPLIERS.filter((m) => config[m.key].enabled).length;
  }, [config]);

  const toggle = (key: keyof MultiplierConfig) => {
    updateMultiplierConfig({ [key]: { ...config[key], enabled: !config[key].enabled } });
  };

  const updateParam = (parentKey: keyof MultiplierConfig, subKey: string, value: number) => {
    updateMultiplierConfig({ [parentKey]: { ...config[parentKey], [subKey]: value } });
  };

  // ── Export / Import ─────────────────────────────────────────

  const handleExport = useCallback(() => {
    const json = JSON.stringify(config, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `multiplier-config-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [config]);

  const handleImport = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result as string);
          // Detect if this is a ScoringConfig (GA output) or MultiplierConfig
          if ("scale" in parsed || "upsetEnabled" in parsed) {
            const mc = scoringConfigToMultiplierConfig(parsed as ScoringConfig);
            updateMultiplierConfig(mc);
          } else {
            updateMultiplierConfig({ ...DEFAULT_MULTIPLIER_CONFIG, ...parsed });
          }
        } catch {
          alert(t("multiplier.invalidFile"));
        }
      };
      reader.readAsText(file);
      e.target.value = "";
    },
    [updateMultiplierConfig],
  );

  const handleReset = useCallback(() => {
    updateMultiplierConfig(DEFAULT_MULTIPLIER_CONFIG);
  }, [updateMultiplierConfig]);

  // ── Recalculate ───────────────────────────────────────────
  const handleRecalculate = useCallback(() => {
    if (!window.confirm(t("multiplier.recalculateConfirm"))) return;
    const matchCount = Object.keys(matchesById).length;
    recalculateAll();
    setRecalcMsg(t("multiplier.recalculated", matchCount));
    setTimeout(() => setRecalcMsg(null), 4000);
  }, [matchesById, t]);

  return (
    <GlassCard>
      <CardHeader>
        <CardTitle>
          🎚️ {t("multiplier.title")}
          <span
            style={{
              fontSize: "0.7rem",
              color: theme.colors.textDim,
              fontWeight: 400,
            }}
          >
            {t("multiplier.active", enabledCount, MULTIPLIERS.length)}
          </span>
        </CardTitle>
      </CardHeader>

      <CardSubtitle>{t("multiplier.description")}</CardSubtitle>

      <ActionRow>
        <PillBtn onClick={handleExport}>{t("multiplier.exportConfig")}</PillBtn>
        <PillBtn onClick={() => fileInputRef.current?.click()}>
          {t("multiplier.importConfig")}
        </PillBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleImport}
          style={{ display: "none" }}
        />
        <PillBtn onClick={handleReset}>{t("multiplier.resetDefaults")}</PillBtn>
        <PillBtnDanger onClick={handleRecalculate}>{t("multiplier.recalculate")}</PillBtnDanger>
      </ActionRow>

      {recalcMsg && (
        <p style={{ fontSize: "0.8rem", color: theme.colors.success, marginBottom: "0.75rem" }}>
          {recalcMsg}
        </p>
      )}

      <ListGap>
        {MULTIPLIERS.map((m) => {
          const { enabled } = config[m.key];
          return (
            <div key={m.key}>
              <ToggleRow on={enabled} onClick={() => toggle(m.key)}>
                <ToggleDot on={enabled}>{enabled ? "✓" : ""}</ToggleDot>
                <ToggleBody>
                  <ToggleLabel on={enabled}>{t(m.labelKey)}</ToggleLabel>
                  <ToggleDesc>{t(m.descKey)}</ToggleDesc>
                </ToggleBody>
              </ToggleRow>

              {enabled && m.params.length > 0 && (
                <div style={{ paddingLeft: "2rem", paddingTop: "0.25rem" }}>
                  {m.params.map((p) => (
                    <div key={p.subKey}>
                      <ParamRow>
                        <ParamLabel>{p.label}</ParamLabel>
                        <GlassNumberInput
                          type="number"
                          min={p.min}
                          max={p.max}
                          step={p.step}
                          value={
                            (config[p.parentKey] as unknown as Record<string, number>)[p.subKey] ??
                            0
                          }
                          onChange={(e) =>
                            updateParam(p.parentKey, p.subKey, Number(e.target.value))
                          }
                        />
                      </ParamRow>
                      {p.hintKey && <ParamHint>💡 {t(p.hintKey)}</ParamHint>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </ListGap>
    </GlassCard>
  );
}
